import { useCallback } from 'react'

import useTracking, { TrackingFnType } from '@hooks/useTracking'
import {
  PianoCheckoutParams,
  PianoTrackingEventParameters,
} from '@components/Piano/types'
import {
  getTrackingCheckoutParams,
  getTrackingParams,
} from '@components/Piano/utils'
import { TiunInteractionType } from '@components/Tiun/types'

type TiunTrackingEventTypeValue =
  | 'tiun_show'
  | 'tiun_click'
  | 'tiun_click_offer'
  | 'start_checkout'
  | 'piano_show'

type TiunEventNameType =
  | 'tiun_start_session_click'
  | 'paid_time_start'
  | 'paid_time_used_10'
  | 'paid_time_end'

interface TiunTrackingEvent {
  type: TiunTrackingEventTypeValue
  params?: PianoTrackingEventParameters
}

const useTiunTrackingEvents = () => {
  const trackingCallbackHandlerWithPianoParams = useCallback<
    TrackingFnType<TiunTrackingEvent>
  >(({ extraData, queryClient }) => {
    const pianoCheckoutParams = getTrackingCheckoutParams(
      queryClient,
    ) as PianoCheckoutParams

    return getTrackingParams({
      type: extraData.type,
      params: {
        ...pianoCheckoutParams,
        ...(extraData.params || {}),
      },
    })
  }, [])

  const trackTiunEventWithPianoParams = useTracking<TiunTrackingEvent>(
    trackingCallbackHandlerWithPianoParams,
  )

  const onTiunSnippetShow = useCallback(() => {
    trackTiunEventWithPianoParams({
      type: 'tiun_show',
    })
  }, [trackTiunEventWithPianoParams])

  const onTiunClick = useCallback(
    (interactionType: TiunInteractionType) => {
      trackTiunEventWithPianoParams({
        type: 'tiun_click',
        params: {
          clickelement: interactionType,
        },
      })
    },
    [trackTiunEventWithPianoParams],
  )

  const onTiunClickOffer = useCallback(() => {
    trackTiunEventWithPianoParams({
      type: 'tiun_click_offer',
      params: {
        buttontext: 'Weiter',
      },
    })
  }, [trackTiunEventWithPianoParams])

  const onStartCheckout = useCallback(
    (element: string) => {
      trackTiunEventWithPianoParams({
        type: 'start_checkout',
        params: {
          clickelement: element,
        },
      })
    },
    [trackTiunEventWithPianoParams],
  )

  const onPianoShow = useCallback(() => {
    trackTiunEventWithPianoParams({
      type: 'piano_show',
    })
  }, [trackTiunEventWithPianoParams])

  const trackingCallbackHandler = useCallback<
    TrackingFnType<{ eventName: TiunEventNameType }>
  >(({ extraData }) => {
    return {
      event: extraData.eventName,
    }
  }, [])

  const trackTiunEvent = useTracking(trackingCallbackHandler)

  const onTiunStartSessionClick = useCallback(
    () => trackTiunEvent({ eventName: 'tiun_start_session_click' }),
    [trackTiunEvent],
  )

  const onPaidTimeStart = useCallback(
    () => trackTiunEvent({ eventName: 'paid_time_start' }),
    [trackTiunEvent],
  )

  const onPaidTimeEnd = useCallback(
    () => trackTiunEvent({ eventName: 'paid_time_end' }),
    [trackTiunEvent],
  )

  return {
    onTiunSnippetShow,
    onTiunClick,
    onTiunClickOffer,
    onStartCheckout,
    onTiunStartSessionClick,
    onPaidTimeStart,
    onPaidTimeEnd,
    onPianoShow,
  }
}

export default useTiunTrackingEvents
